import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Link2 } from 'lucide-react'
import Layout from '../../components/Layout'
import { valuationService } from '../../services/valuationService'
import { LAYOUT_OPTIONS } from '../../constants/propertyConstants'
import PropertyConditionOptions from '../../components/PropertyConditionOptions'
import ValuationWidget from './ValuationWidget'
import ListingMap from '../../components/ListingMap'
import { DEFAULT_SEARCH_RADIUS_METERS, getValuationSearchRadius } from '../../helpers/valuationSearchRadius'
import { VALUATION_METHODS } from '../../helpers/valuationMethods'
import { FLOOR_CONDITION, getValuationConditionProfile } from '../../helpers/valuationConditions'

const NewValuation = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { valuationId } = useParams()
  const isEditMode = !!valuationId
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null })
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [valuationData, setValuationData] = useState(null)
  const [calculationMethod, setCalculationMethod] = useState(VALUATION_METHODS.SIMILARITY_ARITHMETIC)
  const [excludedPropertyIds, setExcludedPropertyIds] = useState([])
  const [comparableWeightOverrides, setComparableWeightOverrides] = useState({})
  const [comparableOverrides, setComparableOverrides] = useState({})
  const [searchRadiusMeters, setSearchRadiusMeters] = useState(DEFAULT_SEARCH_RADIUS_METERS)
  const [sourceProperty, setSourceProperty] = useState(null)
  const [attachSourceProperty, setAttachSourceProperty] = useState(false)
  const addressChangedByUserRef = useRef(false)
  const prefillAppliedRef = useRef(false)
  const [newValuation, setNewValuation] = useState({
    address: '',
    layout: '',
    m2: '',
    condition: '',
    floor: '',
    hasElevator: '',
    underRoof: false,
    notes: '',
  })

  // Fetch existing valuation data in edit mode
  useEffect(() => {
    if (!isEditMode) return

    const fetchValuation = async () => {
      try {
        setLoading(true)
        const valuation = await valuationService.getValuation(valuationId)
        addressChangedByUserRef.current = false
        setNewValuation({
          address: valuation.address || '',
          layout: valuation.layout || '',
          m2: valuation.m2 || '',
          condition: valuation.condition || '',
          floor: valuation.floor || '',
          hasElevator: valuation.hasElevator === true ? 'true' : valuation.hasElevator === false ? 'false' : '',
          underRoof: Boolean(valuation.underRoof),
          notes: valuation.notes || '',
        })
        setCalculationMethod(valuation.calculationMethod || VALUATION_METHODS.SIMILARITY_ARITHMETIC)
        if (valuation.coordinatesLat && valuation.coordinatesLng) {
          setCoordinates({ lat: valuation.coordinatesLat, lng: valuation.coordinatesLng })
        }
        setExcludedPropertyIds(valuation.excludedPropertyIds || [])
        setComparableWeightOverrides(valuation.comparableWeightOverrides || {})
        setComparableOverrides(valuation.comparableOverrides || {})
        setSearchRadiusMeters(getValuationSearchRadius(valuation))
        setSourceProperty(valuation.sourceProperty || null)
        setAttachSourceProperty(Boolean(valuation.sourceProperty?.propertyId))
      } catch (error) {
        console.error('Error fetching valuation:', error)
        setError('Chyba při načítání odhadu')
      } finally {
        setLoading(false)
      }
    }

    fetchValuation()
  }, [valuationId, isEditMode])

  useEffect(() => {
    if (isEditMode || prefillAppliedRef.current) return
    const prefill = location.state?.prefill
    if (!prefill) return

    prefillAppliedRef.current = true
    addressChangedByUserRef.current = false
    const { sourceProperty: prefilledSourceProperty, coordinates: prefilledCoordinates, calculationMethod: prefilledMethod, ...formPrefill } = prefill
    setNewValuation((current) => ({ ...current, ...formPrefill, notes: formPrefill.notes || current.notes }))
    if (prefilledSourceProperty?.propertyId) {
      setSourceProperty(prefilledSourceProperty)
      setAttachSourceProperty(true)
    }
    if (prefilledCoordinates?.lat && prefilledCoordinates?.lng) setCoordinates(prefilledCoordinates)
    if (prefilledMethod) setCalculationMethod(prefilledMethod)
  }, [isEditMode, location.state])

  // Geocode address when it changes
  useEffect(() => {
    if (!addressChangedByUserRef.current || !newValuation.address.trim()) return

    let cancelled = false

    const timeoutId = setTimeout(() => {
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder()
        geocoder.geocode({ address: newValuation.address }, (results, status) => {
          if (cancelled) return
          if (status === 'OK' && results[0]) {
            const location = results[0].geometry.location
            const coords = { lat: location.lat(), lng: location.lng() }
            setCoordinates(coords)
          } else {
            console.warn('Geocoding failed:', status)
            setCoordinates({ lat: null, lng: null })
          }
        })
      }
    }, 1000) // Debounce for 1 second

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [newValuation.address])

  const handleAddressChange = (event) => {
    addressChangedByUserRef.current = true
    setCoordinates({ lat: null, lng: null })
    setNewValuation((current) => ({ ...current, address: event.target.value }))
  }

  // Handle valuation data change from ValuationWidget
  const handleValuationDataChange = useCallback((data) => {
    setValuationData(data)
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!coordinates.lat || !coordinates.lng) {
      setError('Prosím vyberte adresu')
      return
    }

    try {
      setSaving(true)
      setError(null)
      const saveData = {
        ...newValuation,
        conditionProfile: getValuationConditionProfile(newValuation.condition),
        hasElevator: newValuation.hasElevator === '' ? null : newValuation.hasElevator === 'true',
        coordinates: coordinates,
        excludedPropertyIds,
        comparableWeightOverrides,
        comparableOverrides,
        valuationData,
        ...(!isEditMode && attachSourceProperty && sourceProperty ? { sourceProperty } : {}),
      }

      console.log('Saving valuation:', saveData)

      let result
      if (isEditMode) {
        result = await valuationService.updateValuation(valuationId, saveData)
        console.log('✅ Valuation updated successfully:', result)
      } else {
        result = await valuationService.createValuation(saveData)
        console.log('✅ Valuation created successfully:', result)
      }

      const createdValuationId = result?._id
      navigate(isEditMode ? `/valuations/${valuationId}` : `/valuations/${createdValuationId}`, {
        state: isEditMode ? { valuationUpdated: true } : { valuationCreated: true },
      })
    } catch (error) {
      console.error('❌ Error saving valuation:', error)
      setError(isEditMode ? 'Chyba při aktualizaci odhadu' : 'Chyba při ukládání odhadu')
    } finally {
      setSaving(false)
    }
  }

  // Create header content for the Layout
  const headerContent = (
    <div className="flex items-center gap-2 sm:gap-4">
      <button onClick={() => navigate('/valuations')} className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md">
        <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
      </button>
      <h1 className="text-base sm:text-2xl font-semibold text-gray-900">{isEditMode ? 'Upravit odhad' : 'Nový odhad'}</h1>
    </div>
  )

  if (loading) {
    return (
      <Layout headerContent={headerContent}>
        <div className="flex justify-center items-center h-64">Načítání...</div>
      </Layout>
    )
  }

  const sourceIsAttached = Boolean(sourceProperty?.propertyId && attachSourceProperty)

  return (
    <Layout headerContent={headerContent}>
      <div className="px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
          {/* Form Section */}
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-sm sm:text-lg font-medium text-gray-900 mb-4 sm:mb-6">Detaily nemovitosti</h2>

            {sourceProperty?.propertyId && !isEditMode && (
              <div className="mb-5 rounded-lg border border-blue-200 bg-blue-50 p-3">
                <label className="flex cursor-pointer items-start gap-3 text-sm text-blue-950">
                  <input
                    type="checkbox"
                    checked={attachSourceProperty}
                    onChange={(event) => setAttachSourceProperty(event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 font-semibold">
                      <Link2 className="h-4 w-4 shrink-0" />
                      Připojit zdrojovou nemovitost
                    </span>
                    <span className="mt-1 block text-xs text-blue-800">
                      {attachSourceProperty
                        ? 'Adresa, dispozice a výměra zůstanou převzaté ze zdrojové nemovitosti.'
                        : 'Odhad bude ruční. Předvyplněné hodnoty zůstanou zachované a lze je upravit.'}
                    </span>
                  </span>
                </label>
                {sourceIsAttached && (
                  <a
                    href={`/detail/${encodeURIComponent(sourceProperty.propertyId)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 flex items-center gap-3 rounded-md border border-blue-100 bg-white p-2 text-blue-800 hover:border-blue-300"
                  >
                    {sourceProperty.imageUrl && <img src={sourceProperty.imageUrl} alt="" className="h-14 w-20 shrink-0 rounded object-cover" />}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{sourceProperty.title || sourceProperty.location || 'Zdrojová nemovitost'}</span>
                      {sourceProperty.location && <span className="block truncate text-xs text-gray-500">{sourceProperty.location}</span>}
                    </span>
                    <ExternalLink className="h-4 w-4 shrink-0" />
                  </a>
                )}
              </div>
            )}

            {sourceIsAttached && isEditMode && (
              <a
                href={`/detail/${encodeURIComponent(sourceProperty.propertyId)}`}
                className="mb-5 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-blue-900 hover:border-blue-400"
              >
                {sourceProperty.imageUrl && <img src={sourceProperty.imageUrl} alt="" className="h-14 w-20 shrink-0 rounded object-cover" />}
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 font-semibold"><Link2 className="h-4 w-4" /> Připojená nemovitost</span>
                  <span className="mt-1 block truncate text-xs">{sourceProperty.title || sourceProperty.location}</span>
                </span>
              </a>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-xs sm:text-sm font-medium text-red-800">Chyba při ukládání</h3>
                    <div className="mt-1 sm:mt-2 text-xs sm:text-sm text-red-700">{error}</div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Adresa</label>
                <input
                  type="text"
                  value={newValuation.address}
                  onChange={handleAddressChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-base"
                  placeholder="Zadejte adresu nemovitosti"
                  required
                  disabled={(loading && isEditMode) || sourceIsAttached}
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Souřadnice</label>
                <input
                  type="text"
                  value={coordinates.lat && coordinates.lng ? `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}` : ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-xs sm:text-base"
                  placeholder="Souřadnice budou načteny automaticky"
                  disabled
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Dispozice</label>
                  <select
                    value={newValuation.layout}
                    onChange={(e) => setNewValuation({ ...newValuation, layout: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-base"
                    required
                    disabled={(loading && isEditMode) || sourceIsAttached}
                  >
                    <option value="">Vyberte dispozici</option>
                    {LAYOUT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">m²</label>
                  <input
                    type="number"
                    placeholder="např. 65"
                    value={newValuation.m2}
                    onChange={(e) => setNewValuation({ ...newValuation, m2: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-base"
                    required
                    disabled={(loading && isEditMode) || sourceIsAttached}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Stav</label>
                  <select
                    value={newValuation.condition}
                    onChange={(e) => {
                      const condition = e.target.value
                      setNewValuation({ ...newValuation, condition })
                      if (condition === FLOOR_CONDITION) setCalculationMethod(VALUATION_METHODS.SIMILARITY_MULTIPLICATIVE)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-base"
                    required
                    disabled={loading && isEditMode}
                  >
                    <option value="">Vyberte stav</option>
                    <option value={FLOOR_CONDITION}>Spodní hranice odhadu</option>
                    <PropertyConditionOptions valuationOnly />
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Patro</label>
                  <input
                    type="text"
                    placeholder="např. 3, přízemí, 1. patro"
                    value={newValuation.floor}
                    onChange={(e) => setNewValuation({ ...newValuation, floor: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-base"
                    disabled={loading && isEditMode}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Výtah</label>
                  <select
                    value={newValuation.hasElevator}
                    onChange={(e) => setNewValuation({ ...newValuation, hasElevator: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-base"
                    disabled={loading && isEditMode}
                  >
                    <option value="">Neuvedeno</option>
                    <option value="true">Ano</option>
                    <option value="false">Ne</option>
                  </select>
                </div>
                <label className="flex items-center gap-3 pt-6 text-xs sm:text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={newValuation.underRoof}
                    onChange={(e) => setNewValuation({ ...newValuation, underRoof: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={loading && isEditMode}
                  />
                  Přímo pod střechou
                </label>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Poznámky</label>
                <textarea
                  value={newValuation.notes}
                  onChange={(e) => setNewValuation({ ...newValuation, notes: e.target.value })}
                  rows="3"
                  placeholder="Dodatečné poznámky o nemovitosti..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-base"
                  disabled={loading && isEditMode}
                />
              </div>

              <div className="flex gap-3 sm:gap-4 pt-3 sm:pt-4">
                <button
                  type="button"
                  onClick={() => navigate(isEditMode ? `/valuations/${valuationId}` : '/valuations')}
                  className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-xs sm:text-base"
                >
                  Zrušit
                </button>
                <button
                  type="submit"
                  disabled={!valuationData?.compsUsed || loading || saving}
                  className={`flex-1 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-base ${
                    !valuationData?.compsUsed || loading || saving ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {saving ? 'Ukládám a přepočítávám…' : loading ? 'Načítání...' : isEditMode ? 'Aktualizovat odhad' : 'Vytvořit odhad'}
                </button>
              </div>
            </form>
          </div>

          {/* Map Section */}
          <div className="bg-white rounded-lg shadow">
            <ListingMap coordinates={coordinates} address={newValuation.address} onCoordinatesChange={setCoordinates} isEditMode={true} loading={loading} showComparables={false} />
          </div>

          {/* Valuation Widget */}
          <ValuationWidget
            coordinates={coordinates}
            layout={newValuation.layout}
            m2={newValuation.m2}
            condition={newValuation.condition}
            conditionProfile={getValuationConditionProfile(newValuation.condition)}
            floor={newValuation.floor}
            hasElevator={newValuation.hasElevator === '' ? null : newValuation.hasElevator === 'true'}
            underRoof={newValuation.underRoof}
            address={newValuation.address}
            excludedPropertyIds={excludedPropertyIds}
            comparableWeightOverrides={comparableWeightOverrides}
            comparableOverrides={comparableOverrides}
            searchRadiusMeters={searchRadiusMeters}
            calculationMethod={calculationMethod}
            onCalculationMethodChange={setCalculationMethod}
            onValuationDataChange={handleValuationDataChange}
          />
        </div>
      </div>
    </Layout>
  )
}

export default NewValuation
